// Copyright (c) 2024 by Audere

import React from "react";
import {
  Box,
  Grid2,
  List,
  ListItem,
  ListItemText,
  SelectChangeEvent,
  Stack,
  Typography,
  capitalize,
} from "@mui/material";
import { ErrorAlert, InfoAlert, WarningAlert } from "./Alerts";
import { createStyleSheet, globalStyles } from "../utils/styles";
import { formatTimestamp, getTSDistance } from "../utils/dates";
import { getLabelTasks } from "../features/label/labelTypes";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  GetConversationResponse,
  LabelingStatus,
  QCConversationData,
} from "../features/api/labelApiSlice";
import AnnotatorBadgeGroup from "./AnnotatorBadgeGroup";
import { priorityChipStyles } from "./SmallChip";
import {
  getUserLabelStatus,
  hasSubmittedLabels,
} from "../features/data/annotatorStatusUtils";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useSelector } from "react-redux";
import LabelerSelect from "./LabelerSelect";
import HeaderButton from "./HeaderButton";
import {
  setConvoLabelers,
  setLabeler,
  updateConvoLabelers,
} from "../features/slices/labelSlice";
import { useAppDispatch, useAppSelector } from "../features/store";
import { getAnnotators } from "../features/data/conversationUtils";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useLabelingSchema } from "../hooks/useSchema";
import { selectCurrentEmail } from "../features/slices/authSlice";
import { logEvent } from "../features/api/apiSlice";
import { LabelingEvent } from "../features/api/logging";

interface ConversationHeaderProps {
  conversation?: GetConversationResponse;
  isLoading: boolean;
  error: FetchBaseQueryError | SerializedError | undefined;
}

const ConversationHeader: React.FC<ConversationHeaderProps> = ({
  conversation,
  isLoading,
  error,
}) => {
  const { labelTypeID, conversationUID } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { email, isQC, role, group, isMonitor } = useCurrentUser();
  const convoLabelers = useAppSelector((state) => state.label.convoLabelers);
  const { labelingTypes, labelingTasks } = useLabelingSchema();
  const labelTasks = getLabelTasks(labelTypeID, labelingTasks, labelingTypes);
  const userEmail = useSelector(selectCurrentEmail);
  const [searchParams] = useSearchParams();
  const offset = searchParams.get("page");

  if (isLoading) return <InfoAlert message="Loading conversation details..." />;
  if (error) return <ErrorAlert error={error} />;
  if (!conversation)
    return (
      <WarningAlert
        message={`${
          isQC ? "QC-ing" : "Labeling"
        } temporarily unavailable for this conversation.`}
      />
    );
  const annotators = getAnnotators(conversation);
  const submitted = hasSubmittedLabels(getUserLabelStatus(annotators, email));
  const handleEditButtonClick = () => {
    if (!labelTypeID || !conversationUID) return;
    logEvent(dispatch, {
      event: isQC
        ? submitted
          ? LabelingEvent.ReviewQC
          : LabelingEvent.StartQC
        : submitted || isMonitor
          ? LabelingEvent.ViewLabels
          : LabelingEvent.AddLabels,
      data: {
        userEmail: email,
        userRole: role,
        userGroup: group,
        conversationUid: conversationUID,
        labelType: labelTypeID,
      },
    });
    navigate(
      `/dashboard/${labelTypeID}/conversation/${conversationUID}/${labelTasks[0].id}/edit?page=${offset}`
    );
  };

  const handleLabelerChange = (event: SelectChangeEvent) => {
    const labeler = conversation.labelers.find(
      (labeler) => labeler.labeler_uid === event.target.value
    );
    const labelers = conversation.labelers.filter(
      (labeler) => labeler.labeler_status === LabelingStatus.label_complete
    );
    if (isQC || isMonitor) {
      dispatch(setLabeler(labeler));
    }
    if (convoLabelers?.[conversation.conversation_uid]) {
      dispatch(
        updateConvoLabelers({
          convoUID: conversation.conversation_uid,
          labeler,
        })
      );
    } else {
      dispatch(
        setConvoLabelers({
          ...(convoLabelers ?? {}),
          [conversation.conversation_uid]: {
            inProgressLabelers: conversation.labelers.filter(
              (labeler) =>
                labeler.labeler_status === LabelingStatus.label_in_progress
            ),
            labelers,
            selectedLabeler: labeler,
          },
        })
      );
    }
  };

  const priorityColor = priorityChipStyles[conversation.priority].color;

  const conversationLabelersData = convoLabelers?.[conversationUID ?? ""];

  const conversationLabelers =
    isQC || isMonitor
      ? (() => {
          const labelers = conversation.labelers.filter(
            (labeler) =>
              labeler.labeler_status === LabelingStatus.label_complete
          );
          const existing = convoLabelers?.[conversation.conversation_uid];
          if (existing?.selectedLabeler) {
            dispatch(setLabeler(existing.selectedLabeler));
            return {
              inProgressLabelers: conversation.labelers.filter(
                (labeler) =>
                  labeler.labeler_status === LabelingStatus.label_in_progress
              ),
              labelers,
              selectedLabeler: existing.selectedLabeler,
            };
          } else {
            const labeler = labelers[0];
            dispatch(setLabeler(labeler));
            dispatch(
              updateConvoLabelers({
                convoUID: conversation.conversation_uid,
                labeler: labeler,
              })
            );
            return {
              inProgressLabelers: conversation.labelers.filter(
                (labeler) =>
                  labeler.labeler_status === LabelingStatus.label_in_progress
              ),
              labelers,
              selectedLabeler: labeler,
            };
          }
        })()
      : conversationLabelersData;

  return (
    <Box
      sx={{
        ...styles.headerBox,
        borderColor: priorityColor,
      }}
    >
      {/* Metadata with conversation information */}
      <ConversationMetadata conversation={conversation} />

      {/* Annotators and statuses */}
      <Box
        aria-label="Annotators for this conversation"
        sx={styles.labelersBox}
      >
        <AnnotatorBadgeGroup
          annotators={
            isMonitor
              ? [...conversation.labelers, ...(conversation.qc ?? [])]
              : annotators
          }
        />
      </Box>

      {/* Starting Review */}
      <Grid2
        container
        spacing={{ xs: 2, md: 3 }}
        columns={{ xs: 4, sm: 8, md: 12 }}
        alignItems={"center"}
      >
        {conversationLabelers &&
          (!isMonitor || conversationLabelers.labelers.length > 0) && (
            <LabelerSelect
              convoLabelers={conversationLabelers}
              qcData={
                isQC
                  ? (conversation as QCConversationData).qc.filter(
                      (qc) => qc.email === userEmail
                    )
                  : isMonitor
                    ? (conversation as QCConversationData).qc
                    : undefined
              }
              handleLabelerChange={handleLabelerChange}
            />
          )}
        {(conversationLabelers?.selectedLabeler ?? (!isQC && !isMonitor)) && (
          <HeaderButton
            handleEditButtonClick={handleEditButtonClick}
            submitted={submitted}
          />
        )}
      </Grid2>
    </Box>
  );
};

const ConversationMetadata: React.FC<{
  conversation: GetConversationResponse;
}> = ({ conversation }) => {
  const {
    conversation_uid: uid,
    priority,
    start_timestamp: timestamp,
  } = conversation;
  return (
    <Box sx={styles.metadataBox}>
      <List aria-label="Conversation details" disablePadding>
        <ListItem>
          <ListItemText
            primary={uid}
            secondary={
              <>
                <Stack>
                  <Typography
                    variant="body2"
                    component="span"
                    color="text.primary"
                    aria-label={formatTimestamp(timestamp)}
                  >
                    {formatTimestamp(timestamp)}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="span"
                    aria-label={getTSDistance(timestamp)}
                  >
                    {getTSDistance(timestamp)}
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  component="span"
                  aria-label={`Priority: ${capitalize(priority)}`}
                >
                  Priority:{" "}
                  <span style={{ color: priorityChipStyles[priority].color }}>
                    {capitalize(priority)}
                  </span>
                </Typography>
              </>
            }
            slotProps={{ secondary: { component: "div" } }}
          />
        </ListItem>
      </List>
    </Box>
  );
};

const styles = createStyleSheet({
  headerBox: {
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    borderLeft: 20,
    borderRadius: 1.5,
    borderStyle: "solid",
    display: "flex",
    gap: 2,
    padding: 2,
  },
  metadataBox: {
    ...globalStyles.flexColumn,
    flexShrink: 0,
  },
  labelersBox: {
    flex: 1,
    minWidth: 300,
    textAlign: "center",
  },
});

export default ConversationHeader;
